import { useRef, useState } from 'react';
import useFunmiyoBook from './hooks/useFunmiyoBook';
import useNewsletters, { subscribeToNewsletter } from './hooks/useNewsletters';
import useOpenPositions from './hooks/useOpenPositions';
import useSchoolLogos from './hooks/useSchoolLogos';
import useTestimonials from './hooks/useTestimonials';
import useWebSettings from './hooks/useWebSettings';
import useWebStats from './hooks/useWebStats';

function ContactStrip({ email, phone, address, call, wa }) {
  return (
    <div className="cstrip">
  <div><h3>Bring Funmath to your school</h3>
  <p>Tell us about your school and we'll walk you through the programme.</p>
  <div className="cmeta"><a className="cm" href={`mailto:${email}`}>{email}</a><a className="cm" href={call}>{phone}</a><span className="cm">{address}</span></div>
  <div className="socs"><a className="soc" href="https://www.linkedin.com/company/76245841/" target="_blank" rel="noopener noreferrer"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg></a><a className="soc" href="https://www.facebook.com/connectfunmiyo/" target="_blank" rel="noopener noreferrer"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg></a><a className="soc" href="https://www.youtube.com/@FUNMIYO" target="_blank" rel="noopener noreferrer"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg></a></div></div>
  <a className="btn b-teal" href={wa} target="_blank" rel="noopener noreferrer">Start a conversation</a>
</div>
  );
}


function OpenPositionsList({ positions, fallbackEmail }) {
  if (!positions?.length) {
    return (
      <p className="lede" style={{ marginTop: '16px' }}>
        No open positions right now. Check back soon, or write to us anytime.
      </p>
    );
  }
  return (
    <div className="jobs">
      {positions.map((job) => {
        const mailto = job.apply_email || fallbackEmail;
        const subject = encodeURIComponent(job.title || 'Application');
        const tags = job.tags || [];
        return (
          <div className="job" key={job.id}>
            <div>
              <h4>{job.title}</h4>
              <div className="jtags">
                {tags.map((tag, i) => (
                  <span className={i === 0 ? 'jtag p' : 'jtag'} key={`${job.id}-${tag}-${i}`}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <a
              className="btn b-soft"
              href={mailto ? `mailto:${mailto}?subject=${subject}` : '#'}
            >
              Apply
            </a>
          </div>
        );
      })}
    </div>
  );
}

function NewsletterSection({ issues }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus({ type: '', message: '' });
    try {
      await subscribeToNewsletter(email);
      setEmail('');
      setStatus({ type: 'ok', message: "Thanks — you're on the list." });
    } catch (err) {
      setStatus({
        type: 'err',
        message: err.message || 'Could not subscribe. Please try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="block last">
      <div className="eyebrow g">Stay in the loop</div>
      <h2 className="mid">Newsletter</h2>
      <p className="lede">
        {issues?.length
          ? 'Get updates from Funmiyo — and download current or past newsletters anytime.'
          : 'Get updates from Funmiyo when the next newsletter goes out.'}
      </p>
      <div className={`news-grid${issues?.length ? '' : ' single'}`}>
        <div className="news-card">
          <h3>Subscribe</h3>
          <p>Leave your email and we’ll send the next newsletter when it goes out.</p>
          <form className="news-form" onSubmit={onSubmit}>
            <input
              type="email"
              name="email"
              required
              placeholder="you@school.edu"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
            <button className="btn b-teal" type="submit" disabled={busy}>
              {busy ? 'Subscribing…' : 'Subscribe'}
            </button>
          </form>
          {status.message ? (
            <p className={status.type === 'ok' ? 'news-msg ok' : 'news-msg err'}>{status.message}</p>
          ) : null}
        </div>
        {issues?.length ? (
          <div className="news-card">
            <h3>Download newsletters</h3>
            <p>Current and historic newsletters.</p>
            <ul className="news-list">
              {issues.map((issue, idx) => (
                <li key={issue.id}>
                  <div>
                    <strong>{issue.title}</strong>
                    {issue.label ? <small>{issue.label}</small> : idx === 0 ? <small>Latest</small> : null}
                  </div>
                  <a className="btn b-soft" href={issue.url} target="_blank" rel="noopener noreferrer" download>
                    Download
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SchoolLogosMarquee({ logos }) {
  if (!logos?.length) return null;
  const loop = [...logos, ...logos];
  // ~8s per logo for one full pass; floor so small sets stay readable
  const durationSec = Math.max(60, logos.length * 8);
  return (
    <div className="block slogans">
      <div className="eyebrow g">Our schools</div>
      <h2 className="mid">Trusted across classrooms.</h2>
      <p className="lede">Schools that already run Funmath Class with us.</p>
      <div className="marquee logos-marquee">
        <div
          className="mtrack logos-track"
          style={{ animationDuration: `${durationSec}s` }}
        >
          {loop.map((logo, i) => (
            <div className="logo-chip" key={`${logo.id}-${i}`}>
              <img src={logo.url} alt={logo.name} loading="lazy" />
            </div>
          ))}
        </div>
      </div>
      <p className="mhint">Hover to pause</p>
    </div>
  );
}

function TestimonialsMarquee({ items }) {
  if (!items?.length) return null;
  // Duplicate only when there are enough cards for a seamless marquee loop
  const loop = items.length > 1 ? [...items, ...items] : items;
  return (
    <>
      <div className="marquee">
        <div
          className="mtrack"
          style={items.length <= 1 ? { animation: 'none' } : undefined}
        >
          {loop.map((item, i) => (
            <div className="q" key={`${item.id}-${i}`}>
              <div className="qstars">{item.stars}</div>
              <blockquote>&ldquo;{item.quote}&rdquo;</blockquote>
              <div className="qwho">
                <img
                  className="qav"
                  src={item.photoUrl}
                  alt={item.authorName}
                  loading="lazy"
                />
                <div>
                  <strong>{item.authorName}</strong>
                  {item.authorTitle ? <small>{item.authorTitle}</small> : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {items.length > 1 ? <p className="mhint">Hover to pause</p> : null}
    </>
  );
}

export default function FunmiyoBook() {
  const rootRef = useRef(null);
  useFunmiyoBook(rootRef);
  const { phone, email, address, priceLabel, wa, call } = useWebSettings();
  const schoolLogos = useSchoolLogos();
  const coverStats = useWebStats();
  const newsletterIssues = useNewsletters();
  const openPositions = useOpenPositions();
  const testimonials = useTestimonials();

  return (
    <div ref={rootRef}>
<div className="book">
  <div className="mtabs"><button className="mtab m0" data-div="0"><span className="mtab-q">Funmiyo</span><span className="mtab-n">Start</span></button><button className="mtab m1" data-div="1"><span className="mtab-q">Why</span><span className="mtab-n">We exist</span></button><button className="mtab m2" data-div="2"><span className="mtab-q">How</span><span className="mtab-n">It works</span></button><button className="mtab m3" data-div="3"><span className="mtab-q">What</span><span className="mtab-n">School say</span></button><button className="mtab m4" data-div="4"><span className="mtab-q">Who</span><span className="mtab-n">We are</span></button></div>
  <div className="leaf" id="leaf">
    <div className="strip" id="strip">
      <div className="strip-cta"><a className="btn b-teal" href={wa} target="_blank" rel="noopener noreferrer">Talk to us</a></div>
      <img className="strip-mark" id="stripmark" src="/assets/funmiyo-logo.png" alt="Funmiyo" />
    </div>
    <div className="sec" id="sec0"><div className="cover">
  <div className="cover-bg"></div><div className="cover-veil"></div>
  <div className="covertop">
    <a className="btn b-pill b-fmca" href="#"><span className="ic">🎯</span>Funmiyo Assessment</a>
    <a className="btn b-pill b-fday" href="#"><span className="ic">🎉</span>Funmath Day</a>
    <a className="btn b-login" href="/login" id="loginBtn" aria-label="Log in to Funmiyo">
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10 17l5-5-5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M15 12H3" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      Login
    </a>
    <a className="btn b-teal" href={wa} target="_blank" rel="noopener noreferrer">Talk to us</a>
  </div>
  <div className="cover-in">
    <img className="cover-logo" src="/assets/funmiyo-logo.png" alt="Funmiyo" />
    <div className="cover-badge"><span className="bdot"></span>NCF-SE 2023 aligned · Covers CBSE CT curriculum · Grades 1–5</div>
    <h1>World's only pedagogy-backed<br />math games built for<br /><span className="bw sch"><span className="t l1">s</span><span className="c l2">c</span><span className="g l3">h</span><span className="t l4">o</span><span className="c l5">o</span><span className="g l6">l</span><span className="t l7">s</span></span></h1>
    <div className="cover-sub"><span className="w">Let our children&nbsp;</span><span className="bw"><span className="t l1">P</span><span className="c l2">l</span><span className="g l3">a</span><span className="t l4">y</span></span><span className="w">&nbsp;</span><span className="bw"><span className="c l1">M</span><span className="g l2">a</span><span className="t l3">t</span><span className="c l4">h</span></span><span className="w">.</span></div>
  </div>
  <div className="cover-stats">
    {coverStats.map((stat) => (
      <div className="cstat" key={stat.label}>
        <b>{stat.value}</b>
        <span>{stat.label}</span>
      </div>
    ))}
  </div>
</div></div>
<div className="sec" id="sec1"><div className="block first">
  <div className="eyebrow c">The transformation</div>
  <h1 className="big">What changes<br />in the classroom.</h1>
  <p className="lede">Same curriculum. Same teacher. Same textbook. A completely different relationship with the subject.</p>
  <div className="transform"><svg viewBox="0 0 900 256" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="A black and white regular classroom transforming into a colourful Funmath class">
<defs>
  <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#4EC1A8" stopOpacity=".18"/><stop offset="1" stopColor="#F5C518" stopOpacity=".10"/></linearGradient>
</defs>

{/* ── LEFT: grey school ── */}
<g opacity=".95" style={{filter: 'grayscale(1)'}}>
  <rect x="34" y="196" width="330" height="3" rx="1.5" fill="#C9C6C0"/>
  <rect x="86" y="96" width="226" height="100" rx="4" fill="#EAE8E4"/>
  <path d="M199 52l124 46H75z" fill="#D6D3CE"/>
  <rect x="186" y="146" width="26" height="50" rx="2" fill="#C9C6C0"/>
  <rect x="112" y="120" width="34" height="28" rx="2" fill="#D6D3CE"/>
  <rect x="252" y="120" width="34" height="28" rx="2" fill="#D6D3CE"/>
  <rect x="176" y="118" width="46" height="20" rx="2" fill="#BFBBB4"/>
  <circle cx="199" cy="82" r="7" fill="#CFCBC5"/>
  {/* flat kids */}
  <g fill="#B8B4AD">
    <circle cx="122" cy="176" r="9"/><rect x="113" y="186" width="18" height="12" rx="4"/>
    <circle cx="156" cy="176" r="9"/><rect x="147" y="186" width="18" height="12" rx="4"/>
    <circle cx="244" cy="176" r="9"/><rect x="235" y="186" width="18" height="12" rx="4"/>
    <circle cx="278" cy="176" r="9"/><rect x="269" y="186" width="18" height="12" rx="4"/>
  </g>
  {/* flat mouths */}
  <g stroke="#8E8A83" strokeWidth="1.6" strokeLinecap="round">
    <path d="M118 178h8"/><path d="M152 178h8"/><path d="M240 178h8"/><path d="M274 178h8"/>
  </g>
  <text x="196" y="234" textAnchor="middle" fontFamily="Nunito,sans-serif" fontSize="30" fontWeight="900" letterSpacing="-0.5" fill="#9A968F">Regular class</text>
</g>

{/* ── ARROW ── */}
<g>
  <path d="M352 224h62M486 224h62" stroke="#F5C518" strokeWidth="5" strokeLinecap="round" strokeDasharray="1 13" opacity=".6"/>
  <circle cx="450" cy="224" r="31" fill="#F5C518"/>
  <path d="M438 224h23m-8.5-8.5l8.5 8.5-8.5 8.5" stroke="#1C1C2E" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
</g>

{/* ── RIGHT: colourful school ── */}
<g>
  <rect x="536" y="196" width="330" height="3" rx="1.5" fill="#4EC1A8" opacity=".45"/>
  <rect x="588" y="96" width="226" height="100" rx="6" fill="url(#sky)"/>
  <rect x="588" y="96" width="226" height="100" rx="6" fill="none" stroke="#4EC1A8" strokeWidth="2.4"/>
  <path d="M701 48l128 50H573z" fill="#4EC1A8"/>
  <rect x="688" y="146" width="26" height="50" rx="3" fill="#EF5350"/>
  <rect x="614" y="118" width="36" height="30" rx="3" fill="#F5C518" opacity=".85"/>
  <rect x="752" y="118" width="36" height="30" rx="3" fill="#F5C518" opacity=".85"/>
  <rect x="676" y="116" width="50" height="22" rx="3" fill="#fff" stroke="#4EC1A8" strokeWidth="1.8"/>
  <text x="701" y="132" textAnchor="middle" fontFamily="Nunito,sans-serif" fontSize="12" fontWeight="900" fill="#4EC1A8">7+2</text>
  <circle cx="701" cy="78" r="9" fill="#F5C518"/>
  <path d="M696 79q5 5 10 0" stroke="#B8941A" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
  {/* happy kids */}
  <g>
    <circle cx="624" cy="174" r="10" fill="#F5C518"/><rect x="614" y="185" width="20" height="13" rx="5" fill="#F5C518" opacity=".55"/>
    <circle cx="660" cy="174" r="10" fill="#EF5350"/><rect x="650" y="185" width="20" height="13" rx="5" fill="#EF5350" opacity=".5"/>
    <circle cx="744" cy="174" r="10" fill="#4EC1A8"/><rect x="734" y="185" width="20" height="13" rx="5" fill="#4EC1A8" opacity=".5"/>
    <circle cx="780" cy="174" r="10" fill="#F5C518"/><rect x="770" y="185" width="20" height="13" rx="5" fill="#F5C518" opacity=".55"/>
  </g>
  <g stroke="#1C1C2E" strokeWidth="1.7" fill="none" strokeLinecap="round" opacity=".7">
    <path d="M619 176q5 5 10 0"/><path d="M655 176q5 5 10 0"/><path d="M739 176q5 5 10 0"/><path d="M775 176q5 5 10 0"/>
  </g>
  {/* game pieces */}
  <g>
    <rect x="686" y="166" width="30" height="20" rx="3" fill="#fff" stroke="#4EC1A8" strokeWidth="1.6" transform="rotate(-7 701 176)"/>
    <circle cx="695" cy="176" r="2.6" fill="#EF5350"/><circle cx="707" cy="176" r="2.6" fill="#F5C518"/>
  </g>
  {/* sparkles */}
  <g fill="#F5C518">
    <path d="M842 74l2.6 6 6 2.6-6 2.6-2.6 6-2.6-6-6-2.6 6-2.6z"/>
    <path d="M566 116l2 4.6 4.6 2-4.6 2-2 4.6-2-4.6-4.6-2 4.6-2z" opacity=".75"/>
    <path d="M830 156l1.8 4 4 1.8-4 1.8-1.8 4-1.8-4-4-1.8 4-1.8z" opacity=".6"/>
  </g>
  <text x="704" y="234" textAnchor="middle" fontFamily="Nunito,sans-serif" fontSize="30" fontWeight="900" letterSpacing="-0.5" fill="#3AAB93">Funmath class</text>
</g>
</svg></div>
  <table className="diff">
    <thead><tr><th className="bad">Regular maths classroom</th><th className="good">Funmath classroom</th></tr></thead>
    <tbody>
      <tr><td><span className="di">😟</span>Students memorise formulas without understanding</td><td className="good"><span className="di">🎮</span>Students explore concepts through play</td></tr>
      <tr><td><span className="di">😴</span>Repetition feels tedious and demotivating</td><td className="good"><span className="di">🔄</span>Repetition is built into every game</td></tr>
      <tr><td><span className="di">🙋</span>Only front-benchers stay engaged</td><td className="good"><span className="di">🤝</span>Every student participates, wins, and learns</td></tr>
      <tr><td><span className="di">📢</span>Teacher talks, students passively listen</td><td className="good"><span className="di">💡</span>Teacher facilitates, children discover</td></tr>
      <tr><td><span className="di">😰</span>Maths anxiety builds year on year</td><td className="good"><span className="di">⭐</span>Maths becomes something to look forward to</td></tr>
    </tbody>
  </table>
</div>

<div className="block">
  <div className="eyebrow c">The problem</div>
  <h2 className="mid">Math has to be learned<br />more than taught.</h2>
  <p className="lede">Teaching is only a small part of it, and teachers do that work well every day. The other is the repeated cognitive effort from children, which is what mathematics genuinely demands — and that part depends entirely on the child.</p>
  <p className="body" style={{marginTop: '18px'}}>Here is what we kept seeing: children are perfectly happy with mathematics in Grades 1 and 2. By Grade 4 or 5, something has broken. <span className="hl">Not because teaching got worse, but because practice got boring.</span></p>
  <p className="body">Activities explain a concept clearly, but that is only a small part of learning. The remaining needs rigorous engagement. And games played well will bring great engagement and joy into the learning, which is exactly what makes children connect with the subject.</p>
  <div className="cards2">
    <div className="card"><h4>Why repetition fails</h4><p>Worksheets get finished, not enjoyed. The child completes the task without ever choosing to engage with it.</p></div>
    <div className="card"><h4>Why games work</h4><p>A game carries its own motivation. Children repeat and engage willingly, play a lot more games than problems, and every round reinforces the concept.</p></div>
  </div>
</div>

<div className="block">
  <div className="eyebrow c">Who it changes things for</div>
  <h2 className="mid">One program.<br />Three people who matter.</h2>
  <p className="lede">It begins with the child. When a student genuinely wants to be involved in the learning process, everything downstream — the teacher's job, the school's outcomes — becomes easier.</p>
  <div className="cards3">
    <div className="stake"><div className="stake-em em-p"><svg viewBox="0 0 48 48" fill="none"><path d="M24 6l19 8-19 8-19-8 19-8z" fill="#F5C518"/><path d="M13 19v9c0 3.6 4.9 6.4 11 6.4S35 31.6 35 28v-9" stroke="#F5C518" strokeWidth="2.6" fill="none" strokeLinecap="round"/><path d="M41 16v9" stroke="#EF5350" strokeWidth="2.4" strokeLinecap="round"/><circle cx="41" cy="27" r="2.6" fill="#EF5350"/><circle cx="24" cy="38" r="5.2" fill="#4EC1A8"/><path d="M24 36.4q2.2 2.4 4.4 0" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" transform="translate(-2.2)"/><path d="M16 47c0-4.2 3.6-6.9 8-6.9s8 2.7 8 6.9" fill="#4EC1A8" opacity=".38"/></svg></div><h4>Students</h4><span className="tag">Play · Practise · Master</span><p>The child who dreaded math starts asking to play again. Every game means 20 to 40 problems solved willingly, concepts reinforced through repetition that never feels like repetition.</p></div>
    <div className="stake"><div className="stake-em em-s"><svg viewBox="0 0 48 48" fill="none"><rect x="7" y="8" width="34" height="22" rx="2.5" fill="#EF5350" opacity=".2"/><rect x="7" y="8" width="34" height="22" rx="2.5" stroke="#EF5350" strokeWidth="2.4"/><path d="M14 16h11M14 21h8" stroke="#EF5350" strokeWidth="2.2" strokeLinecap="round"/><circle cx="32" cy="19" r="3.2" fill="#F5C518"/><circle cx="24" cy="36" r="4.6" fill="#EF5350"/><path d="M15 46c0-5 4-8 9-8s9 3 9 8" fill="#EF5350" opacity=".33"/></svg></div><h4>Teachers</h4><span className="tag">Engage · Teach · Inspire</span><p>Ready-to-use kits, a dedicated Funmath expert, and continuous support. Complements your lesson plan; makes your children wait for your class, and every child participates, not just the front row.</p></div>
    <div className="stake"><div className="stake-em em-t"><svg viewBox="0 0 48 48" fill="none"><path d="M24 5L42 15v3H6v-3L24 5z" fill="#4EC1A8"/><rect x="10" y="20" width="28" height="20" rx="2" fill="#4EC1A8" opacity=".26"/><rect x="15" y="26" width="6" height="6" rx="1" fill="#4EC1A8"/><rect x="27" y="26" width="6" height="6" rx="1" fill="#4EC1A8"/><rect x="20" y="34" width="8" height="6" rx="1" fill="#EF5350"/><circle cx="24" cy="13" r="2.4" fill="#F5C518"/><path d="M8 41h32" stroke="#4EC1A8" strokeWidth="2.6" strokeLinecap="round"/></svg></div><h4>School owners &amp; principals</h4><span className="tag">Transform · Progress · Retain</span><p>A measurable edge — a maths classroom students look forward to, minimal-tech implementation, real progress tracking, and a story worth sharing with parents. Ultimately, create a math-friendly campus for all your school students.</p></div>
  </div>
</div>
<div className="nextsec"><button className="nextbtn" data-go="2"><div><span className="nl">Next</span><span className="nt">How it works</span></div><span className="nextarrow"><svg viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg></span></button></div></div><div className="sec" id="sec2"><div className="block first">
  <div className="eyebrow">The method</div>
  <h1 className="big">A progressive<br />learning journey.</h1>
  <p className="lede">Every Funmath session follows a four-stage design that carries a child from first encounter to genuine mastery.</p>
  <figure className="film">
    <img src="/assets/img-03.gif" alt="Children playing a Funmath game during class at their desks" />
    <figcaption className="film-cap">
      <div>
        <span className="film-tag">Inside a Funmath class</span>
        <p>This is what curiosity looks like when math feels genuinely engaging.</p>
      </div>
      <span className="film-note">Grade 3 · Kerala</span>
    </figcaption>
  </figure>
  <div className="flow" style={{marginTop: '28px'}}>
    <div className="fstep f1"><div className="fnum">1</div><h4>Curriculum-aligned</h4><p>Every game maps directly to NCERT standards and learning objectives for Grades 1–5.</p></div>
    <div className="fstep f2"><div className="fnum">2</div><h4>Cognitively progressive</h4><p>Games are built at different complexity levels, supporting gradual skill development.</p></div>
    <div className="fstep f3"><div className="fnum">3</div><h4>Reflective</h4><p>Pre-game activities reinforce concepts and ready the child to play a game fully.</p></div>
    <div className="fstep f4"><div className="fnum">4</div><h4>Practice-rich</h4><p>Students master skills through engaging repetition without the monotony of drill and practice.</p></div>
  </div>
  <div className="cards2">
    <div className="card"><h4>For students</h4><p>Sustained motivation through play · mathematical thinking and reasoning · better retention · collaborative learning · progress at their own pace.</p></div>
    <div className="card"><h4>For teachers</h4><p>Ready-to-implement materials · full training and year-long support · real-time tracking via the Funmiyo App · development in game-based pedagogy. Classes become the student’s need.</p></div>
  </div>
</div>

<div className="block">
  <div className="eyebrow">Implementation</div>
  <h2 className="mid">Five visits.<br />A full year of support.</h2>
  <p className="lede">We don't hand over a box and leave. Each visit has a defined purpose across the academic year.</p>
  <div className="visits">
    <div className="visit on"><button className="vhead"><div className="vnum">01</div><div><div className="vt">Teacher training</div><div className="vs">May</div></div><span className="vsign">+</span></button><div className="vbody"><div className="vin"><ul><li>3-hour intensive session at programme launch</li><li>Introduction to game-based pedagogy and Funmath methodology</li><li>Hands-on practice with every grade-level game</li><li>WhatsApp support group opens for real-time help</li></ul></div></div></div>
    <div className="visit"><button className="vhead"><div className="vnum">02</div><div><div className="vt">Collaborative class</div><div className="vs">June–July</div></div><span className="vsign">+</span></button><div className="vbody"><div className="vin"><ul><li>Live demonstration by a Funmath expert in your classroom</li><li>Teacher observes and co-facilitates alongside the expert</li><li>Students experience a full game session firsthand</li><li>Post-session debrief with the school team</li></ul></div></div></div>
    <div className="visit"><button className="vhead"><div className="vnum">03</div><div><div className="vt">Observation &amp; Enhancement</div><div className="vs">August–September</div></div><span className="vsign">+</span></button><div className="vbody"><div className="vin"><ul><li>An expert observes teacher-led sessions and gives constructive feedback</li><li>Actionable steps for progress are shared with the principal and individual teachers</li><li>Video submission and feedback loop for improvement</li></ul></div></div></div>
    <div className="visit"><button className="vhead"><div className="vnum">04</div><div><div className="vt">Performance review</div><div className="vs">October–November</div></div><span className="vsign">+</span></button><div className="vbody"><div className="vin"><ul><li>Review of programme performance and student outcomes</li><li>Corrective plan if gaps appear</li><li>Principals see real-time reports</li></ul></div></div></div>
    <div className="visit"><button className="vhead"><div className="vnum">05</div><div><div className="vt">Final review</div><div className="vs">December–January</div></div><span className="vsign">+</span></button><div className="vbody"><div className="vin"><ul><li>Full programme evaluation and outcome analysis</li><li>Holistic mathematics progress cards prepared for every student</li><li>Planning for the next academic year</li></ul></div></div></div>
  </div>
</div>

<div className="block">
  <div className="eyebrow">What arrives</div>
  <h2 className="mid">The kit and the app.</h2>
  <div className="two top" style={{marginTop: '18px'}}>
    <div>
      <figure className="fig"><img src="/assets/img-04.jpg" alt="Grade 2 Funmath Kit" /><figcaption>A complete Grade 2 kit — Play &amp; Learn game book, seven graded card decks, pebbles, pawns, dice and a storage box.</figcaption></figure>
      <table className="ktab"><thead><tr><th>Grade</th><th>Games</th></tr></thead><tbody>
        <tr><td>Grade 1</td><td>9 board + 6 card games</td></tr>
        <tr><td>Grade 2</td><td>8 board + 7 card games</td></tr>
        <tr><td>Grade 3</td><td>16 games total</td></tr>
        <tr><td>Grade 4</td><td>15 games total</td></tr>
      </tbody></table>
    </div>
    <div>
      <figure className="fig"><img src="/assets/img-05.jpg" alt="Funmiyo App dashboard" /><figcaption>The Funmiyo App — game preparation guides, implementation tracking, video upload for teacher reflection, and a dashboard for administrators.</figcaption></figure>
      <div className="card" style={{marginTop: '14px'}}><h4>Support that continues</h4><p>Dedicated WhatsApp group · assigned Funmath expert per school · monthly online follow-ups · video feedback loop · real-time progress reports for principals · holistic progress cards for parents at year-end.</p></div>
    </div>
  </div>
</div>

<div className="block">
  <div className="eyebrow">Investment</div>
  <h2 className="mid">One fee. Everything in it.</h2>
  <p className="lede">Transparent, all-inclusive pricing that can sit inside your textbook package or yearly fee.</p>
  <div className="price">
    <div>
      <h3>No hidden costs.<br />No additional fees.</h3>
      <p>Everything below is covered by the single annual figure.</p>
      <div className="plist">
        <div className="pchk">Grade-specific Funmath Kit</div><div className="pchk">Full teacher training</div>
        <div className="pchk">Five structured school visits</div><div className="pchk">WhatsApp support group</div>
        <div className="pchk">Funmiyo App access</div><div className="pchk">Annual Math Competition</div>
        <div className="pchk">Progress tracking &amp; reporting</div><div className="pchk">Holistic progress cards</div>
        <div className="pchk">CT skill outcome assurance</div>
      </div>
    </div>
    <div className="ptag"><b>{priceLabel}</b><span>per student per year</span></div>
  </div>
  <ContactStrip email={email} phone={phone} address={address} call={call} wa={wa} />
</div>
<div className="nextsec"><button className="nextbtn" data-go="3"><div><span className="nl">Next</span><span className="nt">What school say</span></div><span className="nextarrow"><svg viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg></span></button></div></div><div className="sec" id="sec3"><div className="block first">
  <div className="eyebrow g">In their words</div>
  <h1 className="big">Principals who<br />said yes first.</h1>
  <p className="lede">196 schools have run Funmath Class. Here is what some of them say about it.</p>
  <TestimonialsMarquee items={testimonials} />
</div>

<div className="block">
  <div className="eyebrow g">On camera</div>
  <h2 className="mid">Hear it from them.</h2>
  <p className="lede">Principals talking about what changed in their classrooms, in their own words.</p>
  <div className="vwrap">
    <div className="vtrack" id="vtrack">
      <div className="vcard" data-vid="lanox6nMTm4">
        <div className="vthumb" role="button" tabIndex="0" aria-label="Play testimonial from Jouhar M">
          <img loading="lazy" src="https://i.ytimg.com/vi/lanox6nMTm4/hqdefault.jpg" alt="Jouhar M, Principal, Peace Public School, Kottakkal" />
          <span className="vplay"><svg viewBox="0 0 24 24" fill="none"><path d="M8 5.5v13l11-6.5-11-6.5z" fill="currentColor"/></svg></span>
        </div>
        <div className="vmeta"><strong>Jouhar M</strong><small>Principal, Peace Public School, Kottakkal</small></div>
      </div><div className="vcard" data-vid="FzIsigZsYpM">
        <div className="vthumb" role="button" tabIndex="0" aria-label="Play testimonial from Yesudas Joseph">
          <img loading="lazy" src="https://i.ytimg.com/vi/FzIsigZsYpM/hqdefault.jpg" alt="Yesudas Joseph, Principal, Wadi Rahma English School" />
          <span className="vplay"><svg viewBox="0 0 24 24" fill="none"><path d="M8 5.5v13l11-6.5-11-6.5z" fill="currentColor"/></svg></span>
        </div>
        <div className="vmeta"><strong>Yesudas Joseph</strong><small>Principal, Wadi Rahma English School</small></div>
      </div><div className="vcard" data-vid="ORh2_Eb6Hm8">
        <div className="vthumb" role="button" tabIndex="0" aria-label="Play testimonial from Mrs. Preethy">
          <img loading="lazy" src="https://i.ytimg.com/vi/ORh2_Eb6Hm8/hqdefault.jpg" alt="Mrs. Preethy, Principal, Paramekkavu Vidya Mandir" />
          <span className="vplay"><svg viewBox="0 0 24 24" fill="none"><path d="M8 5.5v13l11-6.5-11-6.5z" fill="currentColor"/></svg></span>
        </div>
        <div className="vmeta"><strong>Mrs. Preethy</strong><small>Principal, Paramekkavu Vidya Mandir</small></div>
      </div>
    </div>
  </div>
  <div className="vnav">
    <button className="varrow" id="vprev" aria-label="Previous video"><svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7"/></svg></button>
    <div className="vdots" id="vdots"></div>
    <button className="varrow" id="vnext" aria-label="Next video"><svg viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg></button>
  </div>
</div>

<SchoolLogosMarquee logos={schoolLogos} />

<div className="block">
  <div className="eyebrow g">Straight answers</div>
  <h2 className="mid">Questions schools ask.</h2>
  <p className="lede">The ones that come up in almost every conversation. Anything else — just write to us.</p>
  <div className="faq"><div className="fitem"><button className="fq"><strong>Will Funmath disrupt our existing timetable?</strong><span className="fsign">+</span></button><div className="fa"><p>No. Teachers plan portions and schedule Funmath classes at their own convenience. Because every game maps to textbook chapters, it slots into maths periods you already run.</p></div></div><div className="fitem"><button className="fq"><strong>Do teachers need prior training?</strong><span className="fsign">+</span></button><div className="fa"><p>No prior training is required. We run a 3-hour intensive on-premises session at launch in May — game-based pedagogy, the Funmath methodology, and hands-on practice with every grade-level game.</p></div></div><div className="fitem"><button className="fq"><strong>Which grades is it designed for?</strong><span className="fsign">+</span></button><div className="fa"><p>Grades 1 to 5, aligned with NCERT learning objectives. Each grade has its own kit with grade-appropriate games.</p></div></div><div className="fitem"><button className="fq"><strong>Will it pair with our existing textbooks?</strong><span className="fsign">+</span></button><div className="fa"><p>Yes. Any NCERT-based textbook pairs with the kit since the core concepts are the same — only the order of game play changes. It also works alongside curriculums like Exceed and Chrysalis.</p></div></div><div className="fitem"><button className="fq"><strong>How is progress measured?</strong><span className="fsign">+</span></button><div className="fa"><p>In-game evaluation guides, computational thinking worksheets, and a built-in 360° holistic mathematics progress card shared with parents at year-end.</p></div></div><div className="fitem"><button className="fq"><strong>Is the kit safe for children?</strong><span className="fsign">+</span></button><div className="fa"><p>Yes. Kits use laminated paper components rather than plastic, and no hazardous materials are included. In case of serious loss we replace components.</p></div></div><div className="fitem"><button className="fq"><strong>How is this different from Abacus?</strong><span className="fsign">+</span></button><div className="fa"><p>Abacus is a mental maths tool focused only on numbers and needs practice at home. Funmath covers numbers, shapes, patterns, measurements and skills — and builds interest inside the classroom.</p></div></div><div className="fitem"><button className="fq"><strong>Are activities and games the same thing?</strong><span className="fsign">+</span></button><div className="fa"><p>No. Activities teach a concept clearly, but that is only about 20% of learning. The remaining 80% needs repetition. Activities are largely one-off; games are repeatable and self-motivating.</p></div></div><div className="fitem"><button className="fq"><strong>Will it add to the teacher's workload?</strong><span className="fsign">+</span></button><div className="fa"><p>No — it usually reduces it. Everything follows the textbook, and students learn out of the motivation to play. Teachers report sessions become less time-intensive and more productive.</p></div></div><div className="fitem"><button className="fq"><strong>Why can't we buy kits for only half a class?</strong><span className="fsign">+</span></button><div className="fa"><p>Each kit is designed for one full year of use by one student. Partial sets mean repeat purchasing, and it breaks the peer-to-peer play that makes the games work.</p></div></div></div>
  <ContactStrip email={email} phone={phone} address={address} call={call} wa={wa} />
</div>
<div className="nextsec"><button className="nextbtn" data-go="4"><div><span className="nl">Next</span><span className="nt">Who we are</span></div><span className="nextarrow"><svg viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg></span></button></div></div><div className="sec" id="sec4"><div className="block first">
  <div className="eyebrow">Who we are</div>
  <div className="two">
    <div className="teamwrap"><img src="/assets/img-16.jpg" alt="Team Funmiyo" /></div>
    <div>
      <h2 className="mid">We started with one stubborn belief.</h2>
      <p className="body"><span className="hl">Mathematics should be something children experience, not fear.</span> That belief started with a question two education professionals kept asking: why do students who love learning in Grade 1 end up dreading maths by Grade 5?</p>
      <p className="body">The answer wasn't smarter teachers or better textbooks. It was repetition — the kind that feels like punishment. We built Funmiyo to fix that.</p>
      <p className="body">Today we work across three states, in 196 schools, with 60,000+ students discovering that <span className="hl">maths can actually be fun.</span></p>
    </div>
  </div>
</div>

<div className="block">
  <div className="eyebrow">What we stand for</div>
  <h2 className="mid">Seven core values.</h2>
  <p className="lede">Not posters on a wall. These are what we hire for, evaluate against, and use in everyday conversation.</p>
  <ol className="vals">
    <li><b>Long-term commitment</b> — We think in decades, not quarters. Building a brand takes time, and we're building relationships with schools that outlast any single academic year.</li>
    <li><b>Passion for education</b> — Learning is lifelong. You don't have to be an education expert to have this passion, but it has to shine through in what we deliver.</li>
    <li><b>Ownership mindset</b> — Beyond responsibility and accountability sits ownership: being one with the company, where work stops feeling like work.</li>
    <li><b>Truth-seeking</b> — We go to the grassroots. Watching children play and taking their feedback beats reading a book on how to design games. Practice to theory, never the reverse.</li>
    <li><b>Radical honesty</b> — Open, transparent, candid communication internally and externally, without fear of judgment.</li>
    <li><b>Child-first approach</b> — Every decision starts with one question: does this make learning better for the child?</li>
    <li><b>Fun at work</b> — We celebrate small wins and believe a team that enjoys its work builds experiences children enjoy too.</li>
  </ol>
</div>

<div className="block">
  <div className="eyebrow">Join us</div>
  <h2 className="mid">Work on something<br />that matters.</h2>
  <p className="lede">A small team on a mission to change how children across India experience mathematics.</p>
  <div className="cards3">
    <div className="card"><h4>Mission-driven</h4><p>Your work contributes directly to better maths outcomes for tens of thousands of children.</p></div>
    <div className="card"><h4>Real ownership</h4><p>Small team, big responsibility. Your ideas ship fast and the impact is visible.</p></div>
    <div className="card"><h4>Radical honesty</h4><p>Open, direct communication. No politics, no pretence.</p></div>
  </div>
  <h3 className="sub" style={{marginTop: '26px', fontSize: 'var(--f5)'}}>Open positions</h3>
  <OpenPositionsList positions={openPositions} fallbackEmail={email} />
  <ContactStrip email={email} phone={phone} address={address} call={call} wa={wa} />
</div>
<NewsletterSection issues={newsletterIssues} />
</div>
  </div>
  <div className="dividers"><button className="dtab d0" data-div="0"><span className="dtab-q">Funmiyo</span><span className="dtab-n">Start</span></button><button className="dtab d1" data-div="1"><span className="dtab-q">Why</span><span className="dtab-n">We exist</span></button><button className="dtab d2" data-div="2"><span className="dtab-q">How</span><span className="dtab-n">It works</span></button><button className="dtab d3" data-div="3"><span className="dtab-q">What</span><span className="dtab-n">School say</span></button><button className="dtab d4" data-div="4"><span className="dtab-q">Who</span><span className="dtab-n">We are</span></button></div>
</div>

    </div>
  );
}
